// src/components/sla/ai/SlaWorkOrderPreviewModal.js
// ─────────────────────────────────────────────────────────────────────────────
// "What would the AI do?" for one work order: projected grade with factor bars, the
// action the sweep would take and why, the ranked candidates, the decision history —
// and supervisor buttons: Escalate, or Escalate + reassign to the AI's pick.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, List, ListIcon, ListItem, Modal, ModalBody,
    ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Skeleton, Stack, Text, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdChevronRight, MdRefresh } from 'react-icons/md';
import slaAiApi from '../../../services/slaAiApi';
import { useSlaAiActions } from '../../../hooks/useSlaAi';
import SlaGradeBadge from './SlaGradeBadge';
import SlaFactorBars from './SlaFactorBars';
import { ACTION_META, GRADE_FACTOR_LABELS, OUTCOME_META, RANK_FACTOR_LABELS, fmtDateTime, fmtMinutes } from './slaAiFormat';

export default function SlaWorkOrderPreviewModal({ workOrderId, isOpen, onClose, onDone }) {
    const [state, setState] = useState({ data: null, loading: false, error: null });
    const actions = useSlaAiActions();
    const toast = useToast();
    const mounted = useRef(true);
    const card = useColorModeValue('purple.50', 'whiteAlpha.100');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const load = useCallback(async () => {
        if (!workOrderId) return;
        setState(s => ({ ...s, loading: true, error: null }));
        try {
            const data = await slaAiApi.preview(workOrderId);
            if (mounted.current) setState({ data, loading: false, error: null });
        } catch (e) {
            if (mounted.current) setState({ data: null, loading: false, error: e.message });
        }
    }, [workOrderId]);

    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

    const escalate = useCallback(async (reassign) => {
        try {
            const res = await actions.escalate(workOrderId, { reassign });
            toast({ title: reassign ? 'Escalated and reassigned' : 'Escalated', description: res.message, status: 'success', duration: 6000, isClosable: true });
            onDone?.(res.data);
            onClose();
        } catch (e) {
            toast({ title: 'Escalation failed', description: e.message, status: 'error', duration: 6000, isClosable: true });
        }
    }, [actions, workOrderId, toast, onDone, onClose]);

    const p = state.data;
    const g = p?.grade;
    const would = p?.would;
    const meta = ACTION_META[would?.action];
    const best = p?.best_candidate;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <HStack><Icon as={MdAutoAwesome} color="purple.500" /><Text>SLA AI · WO #{workOrderId}</Text></HStack>
                    {p?.work_order && <Text fontSize="sm" fontWeight="normal" color={muted}>{p.work_order.title} · {p.work_order.priority} · deadline {fmtDateTime(p.work_order.resolution_deadline)} · {p.work_order.assignee?.name || 'unassigned'}</Text>}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {state.error && <Alert status="error" borderRadius="lg" mb={3}><AlertIcon /><AlertDescription flex="1">{state.error}</AlertDescription><Button size="xs" leftIcon={<MdRefresh />} onClick={load}>Retry</Button></Alert>}
                    {state.loading && !p && <Stack><Skeleton h="90px" borderRadius="lg" /><Skeleton h="160px" borderRadius="lg" /></Stack>}
                    {p && (
                        <Stack spacing={5}>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <Box>
                                    <Text fontSize="xs" fontWeight="800" color={muted} mb={2}>{g?.projected ? 'PROJECTED GRADE (NOT DONE YET)' : 'GRADE'}</Text>
                                    {g?.has_sla ? (
                                        <HStack align="start" spacing={4}>
                                            <SlaGradeBadge grade={g.grade} score={g.score} size="lg" projected={g.projected} />
                                            <Box flex="1">
                                                <Badge colorScheme={(OUTCOME_META[g.outcome] || {}).scheme}>{(OUTCOME_META[g.outcome] || {}).label || g.outcome}</Badge>
                                                <Text fontSize="sm" mt={1}>{g.minutes_late > 0 ? `${fmtMinutes(g.minutes_late)} past the deadline` : `${Math.round(g.elapsed_pct)}% of the SLA window used`}</Text>
                                                <Text fontSize="xs" color={muted}>{g.engine === 'ml' ? 'ML service' : 'Node mirror'} · {g.model_version}</Text>
                                            </Box>
                                        </HStack>
                                    ) : <Text color={muted}>This job has no SLA deadline.</Text>}
                                </Box>
                                {g?.has_sla && <SlaFactorBars factors={g.components} labels={GRADE_FACTOR_LABELS} weights={g.weights} compact />}
                            </SimpleGrid>

                            <Box bg={card} borderRadius="xl" p={4}>
                                <HStack mb={2}>
                                    {meta ? <Icon as={meta.icon} color={`${meta.scheme}.500`} /> : <Icon as={MdAutoAwesome} color="purple.500" />}
                                    <Text fontWeight="800">{meta ? `On the next sweep the AI would: ${meta.label.toLowerCase()}` : 'No action needed right now'}</Text>
                                    <Badge>{p.mode}</Badge>
                                </HStack>
                                {would?.summary && <Text fontSize="sm" mb={2}>{would.summary}</Text>}
                                <List spacing={1}>
                                    {(would?.reasons || []).map((r, i) => <ListItem key={i} fontSize="sm" display="flex"><ListIcon as={MdChevronRight} color="purple.400" mt="3px" />{r}</ListItem>)}
                                </List>
                            </Box>

                            {best && (
                                <Box>
                                    <Text fontSize="xs" fontWeight="800" color={muted} mb={2}>AI PICK IF REASSIGNED · {p.candidates.length} RANKED · {Math.round((p.confidence || 0) * 100)}% CONFIDENCE</Text>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        <Box>
                                            <Text fontWeight="800">{best.name} <Badge ml={1}>{best.type}</Badge> <Badge colorScheme="purple">{best.score}</Badge></Text>
                                            <List spacing={1} mt={2}>{best.reasons.map((r, i) => <ListItem key={i} fontSize="sm" display="flex"><ListIcon as={MdChevronRight} color="purple.400" mt="3px" />{r}</ListItem>)}</List>
                                            {p.candidates.slice(1, 4).map(c => <Text key={c.id} fontSize="xs" color={muted} mt={1}>Next: {c.name} · {c.score}</Text>)}
                                        </Box>
                                        <SlaFactorBars factors={best.factors} labels={RANK_FACTOR_LABELS} weights={{ reliability: 0.35, capacity: 0.2, proximity: 0.2, experience: 0.15, team: 0.1 }} compact />
                                    </SimpleGrid>
                                </Box>
                            )}
                            {!best && p.excluded?.length > 0 && <Text fontSize="sm" color={muted}>Nobody available: {[...new Set(p.excluded.map(e => e.why))].join('; ')}.</Text>}

                            {p.decisions?.length > 0 && (
                                <Box>
                                    <Text fontSize="xs" fontWeight="800" color={muted} mb={2}>HISTORY</Text>
                                    <Stack spacing={1}>
                                        {p.decisions.map(d => <Flex key={d.id} fontSize="sm" gap={2}><Badge colorScheme={(ACTION_META[d.decision_type] || {}).scheme}>{(ACTION_META[d.decision_type] || {}).short || d.decision_type}</Badge><Text flex="1" noOfLines={1}>{d.summary}</Text><Text color={muted} whiteSpace="nowrap">{fmtDateTime(d.created_at)}</Text></Flex>)}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    )}
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                    <Button variant="outline" colorScheme="red" onClick={() => escalate(false)} isDisabled={!p} isLoading={actions.busy === `escalate:${workOrderId}`}>Escalate</Button>
                    <Button colorScheme="purple" onClick={() => escalate(true)} isDisabled={!best} isLoading={actions.busy === `escalate:${workOrderId}`}>Escalate &amp; reassign{best ? ` to ${best.name}` : ''}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
